import { useUpdate } from '../../../../hooks'

/**
 * Hook for tracking the opening and closing of Google reCAPTCHA modal windows.
 *
 * Google reCAPTCHA dynamically creates a container div with `position: absolute; top: -10000px`
 * when the captcha is triggered. Its visibility is controlled via the `visibility` CSS property.
 *
 * This hook uses a MutationObserver to:
 * 1. Detect when a new captcha container div is added to the DOM.
 * 2. Observe changes to its `style.visibility` attribute.
 * 3. Call `onOpen` when the captcha is displayed (`visibility: visible`).
 * 4. Call `onClose` when the captcha is hidden (`visibility: hidden`), then disconnects the observer.
 *
 * This avoids attaching observers to outdated or closed captcha containers.
 *
 * @param {Object} options
 * @param {Function} [options.onOpen]  - Callback fired when the captcha window opens.
 * @param {Function} [options.onClose] - Callback fired when the captcha window closes.
 */
export function useAuthCaptchaWindowWatcher({ onClose, onOpen }) {
  useUpdate(() => {
    let styleObserver = null

    function watchContainer(container) {
      if (container) {
        // Watch for style changes on visibility only
        styleObserver = new MutationObserver(() => {
          const visibility = container.style.visibility

          if (visibility === 'visible') {
            onOpen?.()
          } else if (visibility === 'hidden') {
            onClose?.()

            // Disconnect observer once captcha closed
            if (styleObserver) {
              styleObserver.disconnect()
              styleObserver = null
            }
          }
        })

        styleObserver.observe(container, {
          attributeFilter: ['style'],
          attributes: true,
        })
      }
    }

    // Watch for new reCAPTCHA container being added to the DOM
    const containerObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (
            node.nodeType === 1 &&
            node.nodeName === 'DIV' &&
            node.style.position === 'absolute' &&
            node.style.top === '-10000px'
          ) {
            watchContainer(node)
          }
        })
      })
    })

    containerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      containerObserver?.disconnect()
      styleObserver?.disconnect()
    }
  }, [])
}
